/**
 * Live-review SSE stream ticket client.
 *
 * Issues a single-use stream ticket from:
 *   POST /api/projects/:projectId/live-review/:reviewId/stream-ticket
 * (Bearer-auth required — same auth pattern as clientReviews.ts).
 *
 * The ticket is then passed as ?ticket= to the SSE endpoint:
 *   GET /api/live-review/:reviewId/stream?ticket=<value>
 * which cannot carry Authorization headers (EventSource limitation).
 *
 * buildStreamUrl() is exported separately so the component can construct
 * the EventSource URL without touching import.meta.env directly
 * (import.meta is not available in Jest/ts-jest's CommonJS mode).
 *
 * createLiveReviewIssue() creates an issue from bridge-click element data.
 * It POSTs to /api/issues with source:'team-review' and
 * the elementData/pinX/pinY derived from the OQ_BRIDGE_CLICK payload.
 */
import { fetchApi, fetchApiEnvelope } from './client';
import { log } from '@/lib/logger';
import type { Issue } from '@overlayqa/shared';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';
import { apiBaseUrl } from '../apiBaseUrl';

log.info('[api/liveReviewStream] Module loaded');

const API_URL = apiBaseUrl();

/**
 * Issue a single-use stream ticket for the given review.
 * Uses Bearer auth (same as all other API calls in the dashboard).
 */
export async function getStreamTicket(
  token: string | undefined,
  projectId: string,
  reviewId: string,
): Promise<string> {
  log.info('[api/liveReviewStream] getStreamTicket', { projectId, reviewId });
  // This ticket route returns { ticket }, without the normal { data } wrapper.
  const result = await fetchApiEnvelope<never>(
    `/projects/${projectId}/live-review/${reviewId}/stream-ticket`,
    { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS },
  );
  if (typeof result.ticket !== 'string' || !result.ticket) throw new Error('Missing review stream ticket');
  return result.ticket;
}

/**
 * Build the SSE EventSource URL for a review stream.
 * Exported so the component can open EventSource without accessing
 * import.meta.env directly (which Jest/ts-jest cannot parse).
 */
export function buildStreamUrl(reviewId: string, ticket: string): string {
  return `${API_URL}/api/live-review/${reviewId}/stream?ticket=${encodeURIComponent(ticket)}`;
}

// ─── Live-review issue creation payload ──────────────────────────────────────

/**
 * Element data shape from OQ_BRIDGE_CLICK / getFullData().
 * Matches elementDataSchema on the server (selector, tagName, className,
 * altText, boundingRect, designMetadata). computedStyles is bridge-internal
 * and not forwarded to the server.
 */
export interface LiveReviewElementData {
  selector: string;
  tagName: string;
  className: string;
  altText?: string;
  titleContext?: import('@overlayqa/shared').IssueTitleCaptureContext;
  fingerprint?: import('@overlayqa/shared').IssueTitleElementData['fingerprint'];
  boundingRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  designMetadata: Record<string, unknown>;
}

/** Persisted issue identity/metadata; only pinned elements can be drawn. */
export type LiveReviewPinnedIssue = Issue & { pageUrl?: string; elementData: LiveReviewElementData };
export async function getLiveReviewIssue(token: string | undefined, issueId: string): Promise<LiveReviewPinnedIssue> {
  return fetchApi<LiveReviewPinnedIssue>(`/issues/${encodeURIComponent(issueId)}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
}
export async function listLiveReviewIssues(token: string | undefined, projectId: string): Promise<LiveReviewPinnedIssue[]> {
  const issues: LiveReviewPinnedIssue[] = [];
  let page = 1;
  for (;;) {
    const result = await fetchApi<{ items: LiveReviewPinnedIssue[]; hasMore?: boolean }>(`/issues?projectId=${encodeURIComponent(projectId)}&pageSize=100&page=${page}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
    issues.push(...(result.items ?? []).filter(issue => typeof issue.elementData?.selector === 'string' && !!issue.elementData.selector));
    if (!result.hasMore) return issues;
    page++;
  }
}

export interface CreateLiveReviewIssuePayload {
  projectId: string;
  title: string;
  source: 'team-review';
  elementData: LiveReviewElementData;
  pinX?: number;
  pinY?: number;
  pinType?: 'element';
  documentDimensionsAtCapture?: { width: number; height: number };
  viewportAtCapture?: { width: number; height: number };
  scrollOffsetAtCapture?: { x: number; y: number };
  screenshotUrl: string;
  pageUrl: string;
  description?: string;
  assigneeId?: string | null;
  severity: Issue['severity'];
  type: Issue['type'];
}

/**
 * Send a command to the bridge via the review command channel.
 *
 * POSTs to POST /api/projects/:projectId/live-review/:reviewId/command.
 * The route (Task 13) accepts { type, ...payload } and forwards to the bridge
 * via the CDP binding registered at startStreamedReview time.
 *
 * @param token     Auth JWT from useAuth().getToken().
 * @param projectId Project the review belongs to.
 * @param reviewId  Active review session ID.
 * @param command   Bridge command payload (type + any extra fields).
 */
export async function sendReviewCommand(
  token: string | undefined,
  projectId: string,
  reviewId: string,
  command: Record<string, unknown>,
): Promise<void> {
  log.info('[api/liveReviewStream] sendReviewCommand', {
    projectId,
    reviewId,
    type: command.type,
  });
  await fetchApi<void>(`/projects/${projectId}/live-review/${reviewId}/command`, {
    token,
    method: 'POST',
    timeoutMs: MUTATION_TIMEOUT_MS,
    body: JSON.stringify(command),
  });
}

/**
 * Send a periodic heartbeat to keep the server-side session's lastActivityAt
 * fresh while the Interactive dashboard surface is open.
 *
 * POSTs to POST /api/projects/:projectId/live-review/:reviewId/heartbeat.
 * Returns silently on success (200 { ok: true }). Callers should swallow
 * errors so a transient network hiccup does not crash the session UI.
 *
 * @param token     Auth JWT from useAuth().getToken().
 * @param projectId Project the review belongs to.
 * @param reviewId  Active review session ID.
 */
export async function sendReviewHeartbeat(
  token: string | undefined,
  projectId: string,
  reviewId: string,
): Promise<void> {
  log.info('[api/liveReviewStream] sendReviewHeartbeat', { projectId, reviewId });
  await fetchApi<{ ok: boolean }>(
    `/projects/${projectId}/live-review/${reviewId}/heartbeat`,
    { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS },
  );
}

/** Close only this authenticated user's streamed session; retrying is safe. */
export async function endLiveReview(token: string | undefined, projectId: string, reviewId: string): Promise<void> {
  await fetchApi(`/projects/${projectId}/live-review/${reviewId}/end`, {
    token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS,
  });
}

/**
 * Create a live-review issue from bridge click element data.
 *
 * LIVE-VERIFY: the server's createIssueSchema (routes/qaIssues.ts) accepts
 * source:'team-review' and elementData (elementDataSchema). Confirm that the
 * payload produces HTTP 201 in integration — Jest mocks cover the client side.
 *
 * @param token   Auth JWT from useAuth().getToken().
 * @param projectId  Project the issue belongs to.
 * @param payload Issue fields including elementData and pin coordinates.
 */
export async function createLiveReviewIssue(
  token: string | undefined,
  projectId: string,
  payload: CreateLiveReviewIssuePayload,
): Promise<Issue> {
  log.info('[api/liveReviewStream] createLiveReviewIssue', {
    projectId,
    selector: payload.elementData.selector,
  });
  return fetchApi<Issue>('/issues', {
    token,
    method: 'POST',
    timeoutMs: MUTATION_TIMEOUT_MS,
    body: JSON.stringify({ ...payload, projectId }),
  });
}
